import { Fragment } from 'react';
import { useQueryState } from 'nuqs';
import {
  Flex,
  FormattedRelativeTime,
  Heading,
  InlineStack,
  Link,
  Stack,
  Tag,
  Text,
  TextLabel,
  Tooltip,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { EnvironmentsEventAttributeSummary, Tag as WorkspaceTag } from '@customerio/hydra-ui-api';
import { capitalize } from '@/utils/string';
import { EventAttributeTags } from '../components/event-attribute-tags';
import { SourcesCell } from '../components/sources-cell';
import { IMMUTABLE_CUSTOMER_ATTRIBUTES } from '@/features/people';
import { shareOf } from '../utils/share';
import { SOURCES_TOOLTIP_CONTENT } from '../utils/sources';
import { SENSITIVE_PRIVACY_LEVEL, type DataIndexItemKind } from '../utils/types';
import { dataIndexItemPath, type DataIndexItemAddress } from '../urls';
import type { DataIndexItemDetail, ItemReach } from './hooks';
import { reachHref, type ItemPageModel } from './page-model';

/**
 * What is known about one item, beside what uses it.
 *
 * The reach line is the point of the panel: how much of the workspace the
 * item actually applies to, and a way straight to that list. Everything else
 * is provenance — when it last arrived, when it was last built on, and where
 * it comes from.
 */

/** What the panel calls the things an item applies to. */
export type ReachTerms = {
  /** "Person" or the object type's own name, singular. */
  singular: string;
  plural: string;
};

/** Label above the value, or beside it in a column of its own. */
export type DetailsLayout = 'stacked' | 'columns';

type DetailsPanelProps = {
  workspaceId: number;
  item: DataIndexItemDetail;
  /**
   * How each row reads. The page this replaces stacks the item page's panel
   * and lays the drawer out in two columns; `stacked` is the default because
   * it is the narrower of the two.
   */
  layout?: DetailsLayout;
  kind: DataIndexItemKind;
  tags: readonly WorkspaceTag[];
  /** How many carry the item, and how many there are altogether. */
  usage: ItemReach;
  reachHref: string;
  terms: ReachTerms;
  /**
   * Whether the reach line is shown at all, and whether it carries a share.
   * A relationship attribute has neither; an event is performed rather than
   * carried, so it is counted but never given a percentage.
   */
  reach: { withRow: boolean; withShare: boolean };
  /** What a workspace calls an automation, for the "last used" explanation. */
  campaignIndefinite: string;
  /**
   * The panel's own "Details" header, on a surface that edits in place —
   * the drawer. Present says the surface edits; `onEdit` says this viewer
   * may. They are separate because the page this replaces heads the panel
   * whenever editing is possible there and drops only the link for a viewer
   * without the permission (`item-details/usage/index.gts:236-253`). The
   * item page passes none: it heads its own panel and edits in a modal.
   */
  header?: { onEdit?: () => void };
  /**
   * The event's own attributes, as a row of the panel. Both surfaces carry
   * it: the page this replaces mounts one details view for the drawer and
   * the item page alike, and gates the row on the item being an event
   * rather than on which surface is asking (`item-details/usage/index.gts:447`,
   * mounted from `data-index-item/index.gts:710`).
   */
  eventAttributes?: ItemEventAttributes;
};

/** An event's own attributes, and the tab that lists all of them. */
export type ItemEventAttributes = {
  attributes: readonly EnvironmentsEventAttributeSummary[];
  allHref: string;
  /** Whether that tab is somewhere else, as it is from the drawer. */
  isLeaving: boolean;
  /** Switches to it in place, where the surface already holds it. */
  onActivate?: () => void;
};

/**
 * What the workspace knows about one item: its tags, how much of the
 * workspace carries it, whether it is redacted, and where it came from.
 *
 * The share itself is `shareOf`'s to round. A workspace with nothing in it
 * has none to report, so the line drops the parenthetical rather than
 * filling it with a dash.
 */
export function DetailsPanel({
  workspaceId,
  item,
  kind,
  layout = 'stacked',
  tags,
  usage: reach,
  reachHref,
  terms,
  reach: reachScope,
  campaignIndefinite,
  header,
  eventAttributes,
}: DetailsPanelProps) {
  const noun = kind === 'event' ? 'event' : 'attribute';

  return (
    <Stack gap="200">
      {header == null ? null : <DetailsHeader onEdit={header.onEdit} />}
      <DetailRow layout={layout} label="Tags">
        <TagList tags={tags} />
      </DetailRow>

      {/* The label is capitalized and the sentence beside it is not, which is
          what the page this replaces does: `{{capitalize (pluralize 2
          usageUnit)}}` for the label, the bare plural inside "4 people (1%)
          have this attribute" (`item-details/usage/index.gts:282,295`). */}
      {reachScope.withRow ? (
        <DetailRow layout={layout} label={capitalize(terms.plural)}>
          <ReachLine reach={reach} href={reachHref} terms={terms} noun={noun} withShare={reachScope.withShare} />
        </DetailRow>
      ) : null}

      {/* Every viewer reads this row, whatever they may change. The page this
          replaces writes `(and hasPermission '…' (eq @item.type 'attribute'))`
          (`item-details/usage/index.gts:313`), which passes the helper as a
          value instead of calling it, so only the type decides. That type is
          a person attribute's alone, and one with no level stored reads "No"
          (`models/attribute.ts:73`). */}
      {kind === 'attribute' ? (
        <DetailRow
          layout={layout}
          label="Sensitive"
          tooltip="If you mark an attribute as sensitive, you can redact it from specific teammates."
        >
          <Text>{item.privacyLevel === SENSITIVE_PRIVACY_LEVEL ? 'Yes' : 'No'}</Text>
        </DetailRow>
      ) : null}

      <ProvenanceRows
        workspaceId={workspaceId}
        layout={layout}
        item={item}
        noun={noun}
        campaignIndefinite={campaignIndefinite}
      />

      <EventAttributesRow layout={layout} kind={kind} eventAttributes={eventAttributes} />
    </Stack>
  );
}

type EventAttributesRowProps = {
  layout: DetailsLayout;
  kind: DataIndexItemKind;
  eventAttributes: ItemEventAttributes | undefined;
};

/** The names an event's payload carries, beside or instead of the tab that lists them. */
function EventAttributesRow({ layout, kind, eventAttributes }: EventAttributesRowProps) {
  if (kind !== 'event' || eventAttributes == null) {
    return null;
  }

  const { allHref, isLeaving, onActivate } = eventAttributes;
  // Newest first, as the page this replaces sorts them for this row on both
  // surfaces (`item-details/usage/index.gts:217-227`): an attribute nobody has
  // sent lately is the least interesting thing on the panel. A missing time
  // sorts last.
  const attributes = [...eventAttributes.attributes].sort((a, b) => (b.last_seen_at ?? 0) - (a.last_seen_at ?? 0));

  return (
    // Title case, as the page this replaces labels the row
    // (`item-details/usage/index.gts:449-451`), so the two apps read the same
    // while the rollout flag decides which one a workspace sees.
    // oxlint-disable-next-line local-rules/content-sentence-case -- verbatim Ember label, kept for flag-on/flag-off parity (FPS-4159)
    <DetailRow layout={layout} label="Event Attributes" labelOffset={attributes.length > 0}>
      {attributes.length === 0 ? (
        <Text>—</Text>
      ) : (
        <EventAttributeTags attributes={attributes} allHref={allHref} isLeaving={isLeaving} onActivate={onActivate} />
      )}
    </DetailRow>
  );
}

type DetailsHeaderProps = {
  /** Absent for a viewer who may read the panel but not write it. */
  onEdit?: () => void;
};

/** The panel's heading, and the way in for whoever may edit it. */
function DetailsHeader({ onEdit }: DetailsHeaderProps) {
  return (
    <InlineStack alignInline="space-between" alignBlock="center">
      <Heading level="2">Details</Heading>
      {onEdit == null ? null : (
        <Link variant="primary" icon="edit" iconPosition="trailing" onClick={onEdit}>
          Edit
        </Link>
      )}
    </InlineStack>
  );
}

type TagListProps = {
  tags: readonly WorkspaceTag[];
};

function TagList({ tags }: TagListProps) {
  if (tags.length === 0) {
    return <Text>—</Text>;
  }

  return (
    <InlineStack gap="050" wrap="wrap">
      {tags.map((tag) => (
        <Tag key={tag.id}>{tag.name}</Tag>
      ))}
    </InlineStack>
  );
}

type ProvenanceRowsProps = {
  workspaceId: number;
  item: DataIndexItemDetail;
  noun: string;
  campaignIndefinite: string;
  layout: DetailsLayout;
};

/** Where the item's data comes from, and when it last moved. */
function ProvenanceRows({ workspaceId, item, noun, campaignIndefinite, layout }: ProvenanceRowsProps) {
  const shownSources = IMMUTABLE_CUSTOMER_ATTRIBUTES.includes(item.name) ? [] : item.sources;
  return (
    <>
      <DetailRow
        layout={layout}
        label="Last seen"
        tooltip={`This is the most recent time the system received an update for this ${noun}.`}
      >
        <RelativeOrDash at={item.lastSeenAt} />
      </DetailRow>

      {item.dailyCount != null && item.dailyCount > 0 ? (
        <DetailRow
          layout={layout}
          label="Today's count"
          tooltip="The number of times this event was received today (since midnight UTC)."
        >
          <Text>{item.dailyCount.toLocaleString()}</Text>
        </DetailRow>
      ) : null}

      <DetailRow
        layout={layout}
        // Title case, as the page this replaces labels the row
        // (`item-details/usage/index.gts:401-403`), so the two apps read the
        // same while the rollout flag decides which one a workspace sees.
        // oxlint-disable-next-line local-rules/content-sentence-case -- verbatim Ember label, kept for flag-on/flag-off parity (FPS-4159)
        label="Last Used"
        tooltip={`This is the most recent time this ${noun} was referenced by a user to build ${campaignIndefinite} or segment.`}
      >
        <RelativeOrDash at={item.lastUsedAt} />
      </DetailRow>

      {/* An attribute the system stamps has no source anyone chose, and the
          page this replaces renders the dash for it in both places, not just
          the list (`app/utils/attributes.ts:61-70`). The nudge reads the same
          list as the marks: a row whose value is a dash must not be spaced as
          though it held a row of circles. */}
      <DetailRow
        layout={layout}
        label="Sources"
        tooltip={SOURCES_TOOLTIP_CONTENT}
        labelOffset={shownSources.length > 0}
      >
        <SourcesCell workspaceId={workspaceId} sources={shownSources} variant="panel" />
      </DetailRow>
    </>
  );
}

type DetailRowProps = {
  label: string;
  tooltip?: string;
  /**
   * Label beside the value, or above it. The page this replaces lays the
   * drawer out in two columns and stacks the item page's narrower panel
   * (`item-details/usage/index.gts:256-262`, its `stack` argument).
   */
  layout: DetailsLayout;
  /**
   * Nudges the label down a step, for a row whose value stands taller than
   * the label box. The page this replaces does it for Sources alone, so the
   * label reads against the first row of marks rather than above them
   * (`item-details/usage/index.gts:420-425`).
   */
  labelOffset?: boolean;
  children: React.ReactNode;
};

/** The label column's width in the two-column layout, as Ember pins it. */
const LABEL_COLUMN_WIDTH = '146px';

function DetailRow({ label, tooltip, layout, labelOffset = false, children }: DetailRowProps) {
  // Sized to its text rather than its column, so the tooltip's underline
  // runs under the words and not across the row.
  const labelText =
    tooltip == null ? (
      <TextLabel>{label}</TextLabel>
    ) : (
      // Beside the label where the label has a column to itself, above it
      // where the value does: a tooltip to the right of a two-column row
      // covers the value it is explaining. The page this replaces switches
      // the same way (`item-details/usage/index.gts:324`).
      <Tooltip content={tooltip} placement={layout === 'stacked' ? 'right' : 'top'} isUnderlined>
        <TextLabel>{label}</TextLabel>
      </Tooltip>
    );

  // The two boxes are the same in both layouts, as they are in the page this
  // replaces: `metaDataKey` is 146 wide and `space300` tall wherever it is
  // used, `metaDataValue` takes the rest, and only the direction between
  // them changes (`data-index-details-drawer/index.css.ts:20-33`). Stacked,
  // the 24px label box is also what keeps the rows on Ember's rhythm.
  const labelBox = (
    <Flex
      alignItems="center"
      gap="100"
      flexShrink="0"
      mt={layout === 'columns' && labelOffset ? '050' : undefined}
      style={{ width: LABEL_COLUMN_WIDTH, height: themeVars.space['300'] }}
    >
      {labelText}
    </Flex>
  );
  const valueBox = (
    <Flex alignItems="center" gap="100" minW="0" style={{ flex: '1 0 0' }}>
      {children}
    </Flex>
  );

  if (layout === 'stacked') {
    return (
      <Stack gap="050">
        {labelBox}
        {valueBox}
      </Stack>
    );
  }

  // No gap between the columns: the label column's width is the whole of the
  // separation, so a value starts at 146px and not a step further in.
  // Aligned to the top and not the middle: a value taller than the 24px
  // label box — a run of marks, a wrapped line — leaves the label where
  // Ember leaves it. One-line rows read the same either way.
  return (
    <Flex alignItems="flex-start" style={{ minHeight: themeVars.space['300'] }}>
      {labelBox}
      {valueBox}
    </Flex>
  );
}

type RelativeOrDashProps = {
  /** Epoch seconds, as the backend records them. */
  at: number | null;
};

function RelativeOrDash({ at }: RelativeOrDashProps) {
  if (at == null || at === 0) {
    return <Text>—</Text>;
  }
  return <FormattedRelativeTime date={at * 1000} withTooltip />;
}

type ReachLineProps = {
  reach: ItemReach;
  href: string;
  terms: ReachTerms;
  noun: string;
  withShare: boolean;
};

/** "1,204 People (38%) have this attribute", linking to that list. */
function ReachLine({ reach, href, terms, noun, withShare }: ReachLineProps) {
  if (reach.withItem == null || reach.withItem === 0) {
    return <Text>—</Text>;
  }

  const unit = reach.withItem === 1 ? terms.singular : terms.plural;
  const share = !withShare || reach.total == null ? null : shareOf(reach.withItem, reach.total);
  const verb = reach.withItem === 1 ? 'has' : 'have';
  const performed = noun === 'event' ? ' performed' : '';

  return (
    <Link href={href} variant="secondary" icon="exit" iconPosition="trailing" target="_blank">
      <Fragment>
        {reach.withItem.toLocaleString()} {unit}
        {share == null ? '' : ` (${share})`} {verb}
        {performed} this {noun}
      </Fragment>
    </Link>
  );
}

type ItemDetailsProps = {
  workspaceId: number;
  address: DataIndexItemAddress;
  item: DataIndexItemDetail;
  tags: readonly WorkspaceTag[];
  /** How many carry the item, and how many there are altogether. */
  reach: ItemReach;
  model: Pick<ItemPageModel, 'reachTerms' | 'reach' | 'campaignIndefinite'>;
};

/**
 * One item's details, wherever they are read: on its own page and beside the
 * list. Both need the same eight answers from the same two reads, so neither
 * of them works them out for itself.
 */
export function ItemDetails({ workspaceId, address, item, tags, reach, model }: ItemDetailsProps) {
  // The same param the panels read, so the link below switches the tab
  // rather than navigating to a URL the router would parse as an event
  // whose name carries the query string.
  const [, setTab] = useQueryState('tab');

  return (
    <DetailsPanel
      workspaceId={workspaceId}
      item={item}
      kind={address.kind}
      tags={tags}
      usage={reach}
      reachHref={reachHref(workspaceId, address, reach.filters)}
      terms={model.reachTerms}
      reach={model.reach}
      campaignIndefinite={model.campaignIndefinite}
      eventAttributes={{
        attributes: item.eventAttributes,
        allHref: `${dataIndexItemPath(String(workspaceId), address.kind, item.name, address.scope)}?tab=attributes`,
        // The tab is on this page, so the link only changes which one is open.
        isLeaving: false,
        onActivate: () => void setTab('attributes'),
      }}
    />
  );
}
